import React, { useState } from 'react';
import { observer } from 'mobx-react';
import A from '@this/components/shared/atoms/a';
import type { FilterableSelectorOption } from '@this/shared/filterable_selector/filterable_selector';
import FilterableSelector from '@this/shared/filterable_selector/filterable_selector';
import type Project from '@this/domain/project/project';
import type OrderItemMapping from '@this/domain/order_item_mapping2';
import type ArrangementRequestWfInfo from '@this/src/domain/arrangement_request_info/arrangement_request_wf_info';
import {
  MappingFormWrapper,
  MappingFormLabel,
  MappingFormDetailArea,
  MappingFormDetailIndentedArea,
  MappingFormDetailItemArea,
  MappingFormDetailSelectArea,
  MappingFormDetailLinkArea
} from './mapping_form.style';

interface Props {
  wf: ArrangementRequestWfInfo;
}

const ProjectMappingForm: React.FC<Props> = ({ wf }) => {
  const [showDetail, setShowDetail] = useState(wf.isForProjectDetail());

  const handleSelectProjectDetail = (
    changingMapping: OrderItemMapping,
    option: FilterableSelectorOption | null
  ) => {
    const values = option?.value.split('-'); // e.g. project-1
    if (values && values?.length === 2) {
      if (values[0] === 'project') {
        changingMapping.projectId = Number(values[1]);
        changingMapping.projectShareId = undefined;
        changingMapping.displayProject = option?.label || '';
      } else if (values[0] === 'share') {
        changingMapping.projectShareId = Number(values[1]);
        changingMapping.projectId = undefined;
        changingMapping.displayProject = option?.label || (option as any)?.labelLines?.join(' ') || '';
      }
    }
  };

  const handleSelect = (selected: FilterableSelectorOption | null): void => {
    const values = selected?.value.split('-'); // e.g. project-1
    if (values && values?.length === 2 && values[0] === 'project') {
      wf.projectId = values[1];
    } else {
      wf.projectId = '';
    }
    wf.orderItems.forEach(item => {
      item.orderItemMappings.forEach(mapping => handleSelectProjectDetail(mapping, selected));
    });
  };

  const createOption = (project: Project) => ({
    label: project.codeAndName(),
    value: `project-${project.id.toString()}`,
    matcher: project.codeAndName()
  });

  const handleShowProjectDetailClick = (value: boolean) => {
    if (!value) wf.resetMappingsProjectId();
    setShowDetail(value);
    wf.showProjectDetail = value;
  };

  const projectOption = () => {
    return wf.projects.list.map(p => ({
      label: p.codeAndName(),
      value: `project-${p.id.toString()}`,
      matcher: p.codeAndName()
    }));
  };

  const projectDetailOption = (): any[] => {
    const projectsOption: any[] = wf.projects.list.map(p => {
      return {
        label: p.codeAndName(),
        value: `project-${p.id.toString()}`,
        matcher: p.codeAndName()
      };
    });
    const sharesOption: any[] = wf.projectShares.list.map(s => {
      return {
        labelLines: s.codeAndNameLines(),
        value: `share-${s.id.toString()}`,
        matcher: s.codeAndNameOneLine()
      };
    });
    return projectsOption.concat(sharesOption);
  };

  return (
    <MappingFormWrapper>
      {showDetail ? (
        <MappingFormDetailArea>
          {wf.orderItems.map((item, item_index) => (
            <div key={`item${item.id}`}>
              <MappingFormLabel>
                {item_index + 1}. {item?.categoryStr}
              </MappingFormLabel>
              <MappingFormDetailIndentedArea>
                {item.travelerInformations.map((traveler: any) => (
                  <MappingFormDetailItemArea key={traveler.id}>
                    <MappingFormLabel>{traveler.name}</MappingFormLabel>
                    <MappingFormDetailSelectArea>
                      <FilterableSelector
                        placeholder="プロジェクトを検索"
                        options={projectDetailOption()}
                        selected={(() => {
                          const mapping = item.orderItemMappings.find(
                            m => m.travelerInformationId === traveler.id
                          );
                          return mapping
                            ? {
                                label: mapping.displayProject || '',
                                value: '',
                                matcher: ''
                              }
                            : null;
                        })()}
                        onSelect={option => {
                          const mapping = item.orderItemMappings.find(
                            m => m.travelerInformationId === traveler.id
                          );
                          if (mapping) {
                            handleSelectProjectDetail(mapping, option);
                          }
                        }}
                      />
                    </MappingFormDetailSelectArea>
                  </MappingFormDetailItemArea>
                ))}
              </MappingFormDetailIndentedArea>
            </div>
          ))}
        </MappingFormDetailArea>
      ) : (
        <div>
          <FilterableSelector
            placeholder="プロジェクトを検索"
            options={projectOption()}
            selected={wf.project ? createOption(wf.project) : null}
            onSelect={handleSelect}
          />
        </div>
      )}
      {wf.projectShareAvailability &&
        (showDetail ? (
          <MappingFormDetailLinkArea>
            <A onClick={() => handleShowProjectDetailClick(false)}>通常設定へ</A>
          </MappingFormDetailLinkArea>
        ) : (
          <MappingFormDetailLinkArea>
            <A onClick={() => handleShowProjectDetailClick(true)}>詳細設定へ</A>
          </MappingFormDetailLinkArea>
        ))}
    </MappingFormWrapper>
  );
};

export default observer(ProjectMappingForm);
